import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearMessage, generateOtp, resetPassword, varifyOtp } from '../../../actions/userActions';
import { FaEye, FaEyeSlash, FaSpinner } from 'react-icons/fa';
import './Forgot.css';

export default function Forgot() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRefs = useRef([]);
  const notificationTimerRef = useRef(null);
  const resendTimerRef = useRef(null);

  // State management
  const [otp, setOtp] = useState(['', '', '', '']);
  const [isStudentLogin, setStudentLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [errors, setErrors] = useState({});
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Redux selectors
  const otpGenerated = useSelector(state => state.user.otpGenerated);
  const otpGeneratedError = useSelector(state => state.user.otpGeneratedError);
  const otpSuccess = useSelector(state => state.user.otpSuccess);
  const forgotPasswordSuccess = useSelector(state => state.user.forgotPassword);
  const forgotPasswordFailure = useSelector(state => state.user.forgotPasswordFailure);
  const verifiedOtp = useSelector(state => state.user.varifiedOtp);
  const verifiedOTPError = useSelector(state => state.user.varifiedOTPError);
  const message = useSelector(state => state.user.message);
  const isLoading = useSelector(state => state.user.loading);

  // Clear notifications
  const clearNotification = () => {
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
    }
    setNotification({ show: false, type: '', message: '' });
  };

  // Show custom notification
  const showCustomNotification = (type, message) => {
    clearNotification();
    setNotification({ show: true, type, message });
    notificationTimerRef.current = setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 3000);
  };

  // Reset all states
  const resetAllStates = () => {
    setOtp(['', '', '', '']);
    setFormData({ email: '', password: '' });
    setEmailVerified(false);
    setOtpVerified(false);
    setEmailDisabled(false);
    setIsButtonDisabled(true);
    setIsEmailValid(false);
    setErrors({});
    setOtpResendTimer(0);
    setIsVerifying(false);
    setIsResetting(false);
    clearNotification();
    dispatch(clearMessage());
  };

  // Toggle between student and admin
  const toggleUserType = (isStudent) => {
    setStudentLogin(isStudent);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });

    if (name === "email") {
      setIsEmailValid(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value));
    }
  };

  // Handle OTP input changes
  const handleOtpChange = (index, value) => {
    if (/[^0-9]/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle keyboard events in OTP inputs
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Start OTP resend timer
  const startResendTimer = () => {
    setOtpResendTimer(120);
    resendTimerRef.current = setInterval(() => {
      setOtpResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Handle sending OTP
  const handleSendOTP = () => {
    if (!isEmailValid) {
      showCustomNotification('warning', 'Please enter a valid email address');
      return;
    }

    if (otpResendTimer > 0) return;

    dispatch(generateOtp({
      email: formData.email,
      isStudent: isStudentLogin,
      newuser: false
    }));
    startResendTimer();
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle OTP verification
  const handleVerifyOTP = () => {
    if (isButtonDisabled) return;
    setIsVerifying(true);
    dispatch(varifyOtp({
      email: formData.email,
      otp: otp.join('')
    }));
  };

  // Handle password reset
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsResetting(true);
      dispatch(resetPassword({
        email: formData.email,
        isStudent: isStudentLogin,
        password: formData.password
      }));
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearNotification();
      if (resendTimerRef.current) {
        clearInterval(resendTimerRef.current);
      }
      dispatch(clearMessage());
    };
  }, [dispatch]);

  // Handle OTP input changes
  useEffect(() => {
    setIsButtonDisabled(!otp.every(value => value !== ''));
  }, [otp]);

  // Handle OTP generation response
  useEffect(() => {
    if (otpGenerated) {
      if (otpSuccess === "key generated") {
        showCustomNotification('success', 'OTP has been sent to your email');
        setEmailVerified(true);
      } else if (otpSuccess === "Email Dosen't Exist in the database") {
        showCustomNotification('error', 'This email is not registered with us');
        setOtpResendTimer(0);
        clearInterval(resendTimerRef.current);
      }
    } else if (otpGeneratedError) {
      showCustomNotification('error', 'Failed to send OTP. Please try again.');
      setOtpResendTimer(0);
      clearInterval(resendTimerRef.current);
    }
    dispatch(clearMessage());
  }, [otpGenerated, otpGeneratedError, otpSuccess, dispatch]); 

  // Handle OTP verification response
  useEffect(() => {
    if (verifiedOtp) {
      setIsVerifying(false);
      if (message === "otp varified") {
        showCustomNotification('success', 'Your OTP has been verified successfully');
        setOtpVerified(true);
        setEmailDisabled(true);
      } else if (message === "otp not varified") {
        showCustomNotification('error', 'The OTP you entered is incorrect');
      }
    } else if (verifiedOTPError) {
      setIsVerifying(false);
      showCustomNotification('error', 'Something went wrong. Please try again.');
    }
    dispatch(clearMessage());
  }, [verifiedOtp, verifiedOTPError, message, dispatch]);

  // Handle password reset response
  useEffect(() => {
    if (forgotPasswordSuccess) {
      showCustomNotification('success', 'Your password has been reset successfully');
      const timer = setTimeout(() => {
        setIsResetting(false);
        resetAllStates();
        navigate('/signin');
      }, 2000);
      return () => clearTimeout(timer);
    } else if (forgotPasswordFailure) {
      setIsResetting(false);
      showCustomNotification('error', 'Failed to reset password. Please try again.');
    }
    dispatch(clearMessage());
  }, [forgotPasswordSuccess, forgotPasswordFailure, navigate, dispatch]);

  return (
    <div className="forgot-container">
      {notification.show && (
        <div className={`custom-notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="forgot-box">
        <div className="forgot-header">
          <h2>{isStudentLogin ? 'Student Password Reset' : 'Creator Password Reset'}</h2>
          <p>Enter your email to reset your password</p>
        </div>

        <div className="user-type-toggle">
          <button
            className={`toggle-btn ${isStudentLogin ? 'active' : ''}`}
            onClick={() => toggleUserType(true)}
            type="button"
          >
            Student
          </button>
          <button
            className={`toggle-btn ${!isStudentLogin ? 'active' : ''}`}
            onClick={() => toggleUserType(false)}
            type="button"
          >
            Creator
          </button>
        </div>

        <form onSubmit={handleSubmit} className="forgot-form">
          <div className="form-group email-group">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email Address"
              disabled={emailDisabled}
            />
            <button
              type="button"
              className="otp-btn"
              onClick={handleSendOTP}
              disabled={emailDisabled || !isEmailValid || otpResendTimer > 0 || isLoading}
            >
              {otpResendTimer > 0 ? (
                `${otpResendTimer}s`
              ) : isLoading ? (
                <FaSpinner className="spin" />
              ) : otpGenerated ? (
                "Sent"
              ) : (
                "Send OTP"
              )}
            </button>
          </div>

          {emailVerified && (
            <div className="form-group otp-group">
              <div className="otp-inputs">
                {otp.map((value, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    value={value}
                    onChange={e => handleOtpChange(index, e.target.value)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    maxLength="1"
                    className="otp-input"
                    disabled={emailDisabled}
                  />
                ))}
              </div>
              <button
                type="button"
                className="verify-btn"
                onClick={handleVerifyOTP}
                disabled={isButtonDisabled || emailDisabled || isVerifying}
              >
                {emailDisabled ? (
                  "Verified"
                ) : isVerifying ? (
                  <>
                    <FaSpinner className="spin" /> Verifying...
                  </>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </div>
          )}

          {otpVerified && (
            <div className="form-group password-group">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="New Password"
                className={errors.password ? 'error-input' : ''}
              />
              <span 
                className="password-toggle" 
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>
          )}

          <button
            type="submit"
            className="submit-btn"
            disabled={!otpVerified || isResetting}
          >
            {isResetting ? (
              <>
                <FaSpinner className="spin" /> Resetting...
              </>
            ) : (
              "Reset Password"
            )}
          </button>

          <div className="login-link">
            Remember your password? <Link to="/signin">Log In</Link>
          </div>
        </form>
      </div>
    </div>
  );
}